import React from 'react'
import Link from 'next/link'
import { FaCheckCircle } from 'react-icons/fa'

/**
 * Two SEO-critical sections that the Singapore personal-trainer SERP competitors
 * (pts.sg, athleaders.co) both cover and our hub page previously did not:
 *
 *  1. "How much does a personal trainer cost in Singapore?" — the #1 co-occurring
 *     subtopic for the head term. Summarised here with a link down to the full
 *     rates guide so the hub owns the query and the child page gets the equity.
 *  2. "How to choose a personal trainer in Singapore" — buyer-intent checklist.
 *
 * Also carries the objection-handling block and the buddy/corporate cross-links
 * that were previously buried on child pages.
 */

const priceRows = [
    {
        option: 'Big-chain gym PT',
        rate: '$120 – $180',
        commitment: 'Membership + lock-in contract',
        highlight: false
    },
    {
        option: 'Boutique studio PT',
        rate: '$110 – $160',
        commitment: 'Studio-bound, peak-hour limits',
        highlight: false
    },
    {
        option: 'Independent freelance PT',
        rate: '$80 – $120',
        commitment: 'Varies — verify credentials yourself',
        highlight: false
    },
    {
        option: 'StringsSG',
        rate: '$84 – $110',
        commitment: 'No membership, no lock-in',
        highlight: true
    }
]

const choosingChecklist = [
    {
        title: 'Check the certification body',
        desc: 'Look for ACE, NASM, NSCA or ACSM — not a weekend course certificate. Ask to see it.'
    },
    {
        title: 'Confirm insurance and company registration',
        desc: 'A registered company carries liability cover. StringsSG is Singapore-registered (UEN 201813375G) and employs its trainers directly.'
    },
    {
        title: 'Ask what happens in session one',
        desc: 'A good trainer assesses your health history, movement and goals first. If they skip straight to selling a package, walk away.'
    },
    {
        title: 'Get the full price in writing',
        desc: 'Watch for peak-hour surcharges, travel fees and add-ons that only appear at checkout. Every StringsSG rate is published upfront.'
    },
    {
        title: 'Understand cancellation and lock-in terms',
        desc: 'Check reschedule notice, whether unused sessions expire, and any multi-month contract.'
    }
]

const objections = [
    {
        emoji: '⏰',
        title: '"I have no time to get to a gym"',
        desc: 'Your trainer travels to you — home, condo gym or a nearby park. No commute, no queuing for equipment.'
    },
    {
        emoji: '🏋️',
        title: '"I don\'t want a gym membership"',
        desc: 'You do not need one. Sessions run wherever you already are, so you pay for coaching and nothing else.'
    },
    {
        emoji: '😬',
        title: '"I\'ve tried before and quit"',
        desc: 'Programmes are built around your actual schedule and fitness level, with progress tracked so you can see it working.'
    }
]

export const PtCostAndChoosing = () => {
    return (
        <section className="relative bg-slate-900 py-6 md:py-16 overflow-hidden">
            <div className="absolute top-0 left-0 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl" />

            <div className="relative z-10 max-w-5xl mx-auto px-4 space-y-8 md:space-y-16">

                {/* ─── Cost comparison ─── */}
                <div>
                    <div className="flex justify-center items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 mx-auto w-fit">
                        <span className="text-sm">💰</span>
                        <span className="text-xs font-medium text-white/70">Pricing</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 text-center tracking-tight">
                        How Much Does a Personal Trainer Cost in Singapore?
                    </h2>
                    <p className="text-white/60 text-sm md:text-base leading-relaxed max-w-3xl mx-auto text-center mb-5">
                        Personal training in Singapore typically runs <strong className="text-white/90">$80 to $180 per session</strong>, depending on
                        where you train and who employs the trainer. Big-chain gyms sit at the top of that range because their rates carry facility
                        overheads and membership fees. Our trainers come to you, so we cut that overhead out.
                    </p>

                    <div className="overflow-x-auto rounded-2xl border border-white/10">
                        <table className="w-full text-left text-sm">
                            <caption className="sr-only">
                                Comparison of personal trainer rates per session in Singapore by provider type
                            </caption>
                            <thead className="bg-white/10 text-white/80">
                                <tr>
                                    <th scope="col" className="px-4 py-3 font-semibold">Option</th>
                                    <th scope="col" className="px-4 py-3 font-semibold whitespace-nowrap">Per session</th>
                                    <th scope="col" className="px-4 py-3 font-semibold">Commitment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {priceRows.map((row) => (
                                    <tr
                                        key={row.option}
                                        className={row.highlight
                                            ? 'bg-orange-500/15 border-t border-orange-400/30'
                                            : 'border-t border-white/10'}
                                    >
                                        <th scope="row" className={(row.highlight ? 'text-orange-300' : 'text-white/80') + ' px-4 py-3 font-semibold text-left'}>
                                            {row.option}
                                        </th>
                                        <td className={(row.highlight ? 'text-orange-300 font-bold' : 'text-white/70') + ' px-4 py-3 whitespace-nowrap'}>
                                            {row.rate}
                                        </td>
                                        <td className="px-4 py-3 text-white/60">{row.commitment}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p className="text-xs text-white/40 mt-3 text-center">
                        Market ranges are indicative and vary by trainer experience, peak/off-peak timing and package size.
                    </p>

                    <div className="text-center mt-6">
                        <Link
                            href="/sg/personal-trainer/personal-trainer-rates-singapore"
                            className="inline-block px-6 py-3 rounded-full bg-linear-to-r from-orange-600 to-amber-500 text-white font-bold shadow-lg hover:opacity-90 transition"
                        >
                            See the full personal trainer rates guide
                        </Link>
                    </div>
                </div>

                {/* ─── How to choose ─── */}
                <div>
                    <div className="flex justify-center items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 mx-auto w-fit">
                        <span className="text-sm">✅</span>
                        <span className="text-xs font-medium text-white/70">Buyer&apos;s Guide</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-5 md:mb-8 text-center tracking-tight">
                        How to Choose a Personal Trainer in Singapore
                    </h2>

                    <ol className="space-y-2.5 md:space-y-4">
                        {choosingChecklist.map((item, i) => (
                            <li
                                key={item.title}
                                className="flex gap-3 md:gap-4 bg-white/5 border border-white/10 rounded-2xl p-3 md:p-5"
                            >
                                <span className="shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-xl bg-linear-to-br from-orange-500 to-amber-400 text-white font-bold flex items-center justify-center text-sm">
                                    {i + 1}
                                </span>
                                <div>
                                    <h3 className="text-white/90 font-semibold text-sm md:text-base mb-0.5">{item.title}</h3>
                                    <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className="text-center mt-6">
                        <Link
                            href="/sg/personal-trainer/choosing-a-personal-trainer-singapore"
                            className="text-orange-400 hover:text-orange-300 font-medium underline underline-offset-4 text-sm"
                        >
                            Read the full guide to choosing a personal trainer
                        </Link>
                    </div>
                </div>

                {/* ─── Objection handling ─── */}
                <div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-5 md:mb-8 text-center tracking-tight">
                        Not Sure Personal Training Is for You?
                    </h2>
                    {/* Emoji sits inline with the heading on mobile so each card is one
                        compact block rather than a tall stacked column. */}
                    <div className="grid md:grid-cols-3 gap-3 md:gap-4">
                        {objections.map((item) => (
                            <div
                                key={item.title}
                                className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5 md:text-center"
                            >
                                <h3 className="text-white/90 font-semibold text-sm mb-1 md:mb-2 flex items-center gap-2 md:flex-col md:gap-0">
                                    <span className="text-xl md:text-3xl md:mb-3">{item.emoji}</span>
                                    <span>{item.title}</span>
                                </h3>
                                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ─── Other training formats ─── */}
                <div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2 md:mb-3 text-center tracking-tight">
                        Other Ways to Train With Us
                    </h2>
                    <p className="text-white/60 text-sm text-center mb-4 md:mb-8 max-w-2xl mx-auto">
                        One-to-one is our most popular format, but it is not the only one.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
                            <h3 className="text-white/90 font-semibold mb-2 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Buddy Training
                            </h3>
                            <p className="text-white/60 text-sm leading-relaxed mb-4">
                                Train with a friend, partner or family member and split the cost, while each of you works to your own goals.
                            </p>
                            <Link
                                href="/sg/personal-trainer/buddy-training"
                                className="text-orange-400 hover:text-orange-300 font-medium underline underline-offset-4 text-sm"
                            >
                                Explore buddy personal training
                            </Link>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
                            <h3 className="text-white/90 font-semibold mb-2 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Female Trainers
                            </h3>
                            <p className="text-white/60 text-sm leading-relaxed mb-4">
                                Prefer to train with a woman? Browse female trainers by area, including coaches who
                                specialise in prenatal, postpartum and women&apos;s health programming.
                            </p>
                            <Link
                                href="/sg/personal-trainer/personal-trainer-singapore-female"
                                className="text-orange-400 hover:text-orange-300 font-medium underline underline-offset-4 text-sm"
                            >
                                Find a female personal trainer in Singapore
                            </Link>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
                            <h3 className="text-white/90 font-semibold mb-2 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Freelance Trainers
                            </h3>
                            <p className="text-white/60 text-sm leading-relaxed mb-4">
                                Independent trainers who come to your home, condo gym or a nearby park, with no
                                gym membership and no lock-in.
                            </p>
                            <Link
                                href="/sg/personal-trainer/freelance-personal-trainer-singapore"
                                className="text-orange-400 hover:text-orange-300 font-medium underline underline-offset-4 text-sm"
                            >
                                Hire a freelance personal trainer in Singapore
                            </Link>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
                            <h3 className="text-white/90 font-semibold mb-2 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Corporate Fitness
                            </h3>
                            <p className="text-white/60 text-sm leading-relaxed mb-4">
                                On-site training and wellness programmes for teams, scheduled around your office hours.
                            </p>
                            <Link
                                href="/sg/personal-trainer/corporate-training"
                                className="text-orange-400 hover:text-orange-300 font-medium underline underline-offset-4 text-sm"
                            >
                                Explore corporate training
                            </Link>
                        </div>
                    </div>
                </div>

            </div>
        </section>
    )
}
